import { App, Form } from 'antd';
import { useRef, useState } from 'react';
import { compressImage } from '~/components/screens/AdDetails/CreationEditionForm/Fieldsets/PhotosGrid/PhotosGrid';
import { useTemplates } from '~/hooks/useConversations';

const ONEMO = 1048576;
const MAX_SIZE_BYTES = 10 * ONEMO;
const MAX_COMPRESSED_SIZE_BYTES = 3 * ONEMO;
const MAX_FILES = 5;

const isFileImage = (fileType) => fileType.match('image.*');

export function useMessageComposer({ mutation, activeMerchantId }) {
  const { notification } = App.useApp();
  const [files, setFiles] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const editorRef = useRef(null);
  const modalEditorRef = useRef(null);

  const { data: templates } = useTemplates(activeMerchantId);

  const applyTemplate = (template, fromDropdown = false) => {
    const targetRef = isModalOpen ? modalEditorRef : editorRef;
    targetRef.current?.setValue(template.content);
    form.setFieldsValue({ content: template.content });
    setSelectedTemplateId(fromDropdown ? null : template.id);
  };

  const removeFile = (fileName) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== fileName));
  };

  const onFinish = (values) => {
    if (!values.content && files.length === 0) return;
    if (files.length > MAX_FILES) {
      notification.open({
        className: 'notification-dialogue-error',
        message: `Vous ne pouvez pas envoyer plus de ${MAX_FILES} fichiers à la fois`,
      });
      return;
    }
    const formData = new FormData();
    if (values.content) {
      formData.append('message', values.content.replaceAll('\r\n', '\n'));
    }
    files.forEach((attachment) => formData.append('attachments', attachment));
    mutation.mutate(formData, {
      onSuccess: () => {
        setFiles([]);
        setSelectedTemplateId(null);
        editorRef.current?.setValue('');
        form.resetFields();
      },
      onError: (error) => {
        notification.open({
          className: 'notification-dialogue-error',
          message: error.message,
        });
      },
    });
  };

  const onDrop = (acceptedFiles) => {
    const newFiles = [];
    acceptedFiles.forEach((file) => {
      if (isFileImage(file.type)) {
        compressImage(file, MAX_COMPRESSED_SIZE_BYTES).then((compressedFile) => {
          setFiles((prevFiles) => [...prevFiles, compressedFile]);
        });
      } else {
        newFiles.push(file);
      }
    });
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => {
    const content = form.getFieldValue('content');
    editorRef.current?.setValue(content || '');
    setIsModalOpen(false);
  };

  const submitForm = () => form.submit();

  const modalProps = {
    open: isModalOpen,
    onClose: closeModal,
    title: 'Nouveau message',
    editorRef: modalEditorRef,
    placeholder: 'Saisissez votre réponse',
    defaultValue: form.getFieldValue('content') || '',
    onChange: (value) => form.setFieldsValue({ content: value }),
    onSubmit: submitForm,
    submitText: 'Envoyer',
  };

  return {
    form,
    editorRef,
    files,
    templates,
    selectedTemplateId,
    onDrop,
    removeFile,
    applyTemplate,
    onFinish,
    openModal,
    submitForm,
    modalProps,
    MAX_SIZE_BYTES,
  };
}
